// START IMPORTS
import { ReportContextProvider } from '@ltvco/refresh-lib/ctx';
import { Stack, Box, useTheme } from '@ltvco/refresh-lib/theme';
import { DateUtil } from '@ltvco/refresh-lib/utils';
import {
  useDarkWebReport,
  useCreateDarkWeb,
  ReportLoading,
  type SerializedDarkWebBreach,
} from '@ltvco/refresh-lib/v1';
import {
  DarkWebBreachTips,
  DarkWebCoverage,
  DarkWebOverview,
  DarkWebResults as DarkWebResultsV2,
  DarkWebScanFAQ as DarkWebFAQV2,
  ReportNavigationMenu,
  ReportNavigationMenuProps,
  ReportNavigationMobileMenu,
  useSession,
} from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { getDarkWebNavLinkData } from 'navLinkData/darkWebNavLinkData';

// END IMPORTS

export function DarkWebScan() {
  // START HOOKS_AND_QUERIES
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const permalinkFromUrl = searchParams.get('permalink')?.trim() ?? '';
  const {
    session: { account },
  } = useSession();
  const [isDarkWebSectionLoading, setIsDarkWebSectionLoading] = useState(false);
  const email = account?.account.user_info?.email ?? '';
  const isCombinedDarkWebEmailFraud = useFeatureIsOn('RFRSH-4333');
  const { data: createDarkWebData } = useCreateDarkWeb({ email });
  const { data: darkWebData, isLoading } = useDarkWebReport({ email });
  const dateUtil = new DateUtil();
  const theme = useTheme();
  // END HOOKS_AND_QUERIES

  // START DERIVED_DATA_AND_UTILS
  const darkWebNavLinkData = getDarkWebNavLinkData(theme);
  const delayDarkWebLoading = 1000;
  const breaches = darkWebData?.data?.breaches ?? [];
  const firstBreachDate = breaches?.[0]?.date ?? '';
  const updatedAt = darkWebData?.data?.rawData?.meta?.updated_at;
  const createdAt = darkWebData?.data?.rawData?.meta?.created_at ?? '';
  const isSensitive = breaches?.some((breach) => breach?.isSensitive);

  const { year } = dateUtil.parseDatePartsFromString(
    createdAt,
    'yyyy-MM-dd HH:mm:ss ZZZ'
  );
  const permalink =
    permalinkFromUrl || createDarkWebData?.report?.permalink || '';
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    document.title = 'Dark Web Scan - NumberGuru';
  }, []);

  useEffect(() => {
    if (isCombinedDarkWebEmailFraud && email) {
      navigate(`/report/email-fraud?email=${encodeURIComponent(email)}`, {
        replace: true,
      });
    }
  }, [isCombinedDarkWebEmailFraud, email, navigate]);

  useEffect(() => {
    setIsDarkWebSectionLoading(true);
    const id = setTimeout(() => {
      setIsDarkWebSectionLoading(false);
    }, delayDarkWebLoading * 10);

    return () => clearTimeout(id);
  }, []);
  // END EFFECTS

  // START EARLY_RETURNS
  if (isCombinedDarkWebEmailFraud && email) {
    return <ReportLoading />;
  }

  if (isLoading) {
    return <ReportLoading />;
  }
  // END EARLY_RETURNS

  // START RENDER
  return (
    <ReportContextProvider permalink={permalink} reportType="dark_web_report">
      <Box marginBottom={2.5}>
        <Stack direction="row">
          <Box
            sx={{
              display: { xs: 'none', lg: 'block' },
            }}
          >
            <ReportNavigationMenu
              reportType="Dark Web Scan"
              headerTitle={email}
              menuItems={
                darkWebNavLinkData as ReportNavigationMenuProps['menuItems']
              }
            />
          </Box>
          <Box marginTop={0} width="100%">
            <Stack
              spacing={2}
              padding={(theme) => ({
                xs: theme.spacing(2.5),
                md: `${theme.spacing(2.5)} 0`,
              })}
            >
              <DarkWebOverview
                email={email}
                updated={updatedAt ?? ''}
                breaches={breaches?.length ?? 0}
                created={firstBreachDate || year?.toString() || 'Unknown'}
                isSensitive={!!isSensitive}
              />
              <DarkWebResultsV2
                email={email}
                breaches={breaches as SerializedDarkWebBreach[]}
                loading={isDarkWebSectionLoading}
                delayDuration={delayDarkWebLoading}
              />
              <DarkWebBreachTips permalink={permalink} />
              <DarkWebFAQV2 />
              <DarkWebCoverage />
            </Stack>
          </Box>
        </Stack>
        <ReportNavigationMobileMenu
          reportType="Dark Web Scan"
          headerTitle={email}
          menuItems={
            darkWebNavLinkData as ReportNavigationMenuProps['menuItems']
          }
        />
      </Box>
    </ReportContextProvider>
  );
}
// END RENDER
